import { useState } from 'react';
import { getGlossaryWord } from '@pm2/api';
import { getCaptionValue } from '@pm2/content-engine';
import { pm2ConfigStore } from '@pm2/store';
import { getTextComponentRoot, generatePdfExportConfig } from '@pm2/text-component';
import { appContext, stringifyPublisher } from '@provider/app-context';
import { useUserIdentityState } from '@provider/user-identity';
import { Tooltip } from '@ui/kit';
import { surfaces } from '@ui/kit/design-tokens';
import { DownloadIcon } from '@ui/kit/icons';
import { printHtmlToText } from '@utils/ast';
import { loadExportLibrary, downloadURI } from '@utils/common';
import { UniversalContextMenuButton } from '@utils/component-framework';
import { jsx } from 'react/jsx-runtime';

function PdfExportButton(props) {
  const user = useUserIdentityState(state => state.user);
  const loginDisabled = pm2ConfigStore.useSelector(state => state.config.settings.loginDisabled);
  const [progress, setProgress] = useState(undefined);
  const handleClick = async () => {
    const {
      publisher,
      product,
      course,
      page
    } = appContext.getState();
    const authors = await appContext.getPageAuthors().catch(err => {
      // log errors, but allow PDF export to continue running even if we could
      // not fetch authors for the page
      console.error(err);
      return undefined;
    });
    try {
      const gExport = await loadExportLibrary();
      const root = getTextComponentRoot(props.togglerRef.current, "data-component");
      if (!root) {
        throw new Error("PDFExportButton: unable to extract root text element");
      }
      const html = gExport.utils.extractWindowHtml(window, root);
      const htmlWithoutNotes = html.replace(/<mark class="[^\s]+">/g, "").replace(/<\/mark>/g, "");
      const pdfExportConfig = await generatePdfExportConfig({
        html: htmlWithoutNotes,
        ref: props.togglerRef.current
      }, {
        template: props.template,
        enableLineCount: props.enableLineCount,
        isRichText: props.node.meta.isRichText,
        title: getCaptionValue(props.node.meta.caption.title),
        publisher,
        productTitle: product?.title,
        pageTitle: course?.title ?? page?.title,
        authors,
        user
      }, async glossaryWordId => printHtmlToText(await getGlossaryWord(product?.id ?? "", glossaryWordId, {
        previewMode: false
      })));
      const output = await gExport.convert({
        ...pdfExportConfig,
        headerTemplate: generateHeaderContent(publisher, product, pdfExportConfig.preset, props.headerImage)
      }).on("progress", status => setProgress(status.progress / 100));
      setProgress(undefined);
      downloadURI(output.url);
    } catch (err) {
      setProgress(undefined);
      alert("Download af PDF er midlertidigt ikke tilgængeligt, vi beklager. Prøv igen senere.");
      console.error(err);
    }
  };
  return /*#__PURE__*/jsx(Tooltip, {
    mountIn: document.body,
    placement: "left",
    disabled: !loginDisabled,
    body: "Denne funktion kan ikke tilgås i øjeblikket.",
    ariaType: "description",
    children: ref => /*#__PURE__*/jsx(UniversalContextMenuButton, {
      ref: ref,
      as: "button",
      label: "Download PDF",
      onClick: handleClick,
      disabled: loginDisabled,
      icon: /*#__PURE__*/jsx(DownloadIcon, {}),
      async: {
        progress
      }
    })
  });
}
function generateHeaderContent(publisher, product, preset, headerImage) {
  const headerHeightPx = 60;
  const headerFontSizePx = 32;
  if (headerImage) {
    return `
      <style>
        #header { padding: 0; }
      </style>

      <img
        src="${headerImage}?h=140"
        alt=""
        style="
          width: 100%;
          height: ${headerHeightPx}px;

          object-fit: contain;
          object-position: top center;
        "
      />
    `;
  }
  const headerSurface = resolveHeaderSurface();
  return `
    <style>
      #header { padding: 0; }
    </style>

    <div style="
      display: flex;
      align-items: center;
      width: 100%;
      height: ${headerHeightPx}px;

      box-sizing: border-box;
      padding: 0 65px;

      background-color: ${headerSurface.background};
      color: ${headerSurface.color};

      -webkit-print-color-adjust: exact;

      font: ${headerFontSizePx}px / 1 helvetica, sans-serif;
      text-transform: uppercase;
    ">
      ${product ? `
            <b style="margin-right: 1ch;">${product.subject}</b>
            ${product.gradesLabel ?? ""}
            ${product.classLabel ?? ""}
          ` : stringifyPublisher(publisher)}
    </div>
  `;
}
function resolveHeaderSurface() {
  const bgProp = `--tmp-${Date.now()}--iframe-export--header-bg`;
  const colorProp = `--tmp-${Date.now()}--iframe-export--header-color`;
  document.documentElement.style.setProperty(bgProp, surfaces.primary({
    property: "background"
  }));
  document.documentElement.style.setProperty(colorProp, surfaces.primary({
    property: "color"
  }));
  try {
    const computedStyle = window.getComputedStyle(document.documentElement);
    return {
      background: computedStyle.getPropertyValue(bgProp),
      color: computedStyle.getPropertyValue(colorProp)
    };
  } finally {
    document.documentElement.style.removeProperty(bgProp);
    document.documentElement.style.removeProperty(colorProp);
  }
}

export { PdfExportButton };
//# sourceMappingURL=pdf-export-button.js.map
